import { anchorMatches, scopeOf, WORD } from './address.js';

// A commentary / notes apparatus panel: given an address, show the notes
// belonging to that stretch of text, with the note(s) covering the selected
// word raised. Four commentaries render through this on the Tosefta site
// (Kifshuta, the Brief Commentary, Chasdei David, Tekhelet Mordechai), so it
// knows nothing about any of them -- the host supplies entries.
//
//   adapter.entriesFor(address) -> [entry]
//       The notes to show for this address, already in reading order. An entry
//       IS an anchor (it carries chapter/halakhah/baseIdx/endIdx directly) plus:
//         id       string  stable, used for highlight/scroll targeting
//         html     string  the note body, trusted HTML from the edition
//         dh       string  the lemma the note opens on, if any
//         lineRef  string  a printed-edition line citation, if any
//         metaLabel string  overrides the derived meta line, for hosts that can
//                           say something more precise about why a note is
//                           unplaced
//       Entries with baseIdx == null are anchored only to their halakhah --
//       a first-class case, not a failed word anchor -- and are rendered as
//       belonging to the passage as a whole.
//
//   adapter.headingFor(address) -> string   (optional) e.g. "פרק ג, הלכה ז"

export const COMMENTARY_LABELS = {
    noSelection: 'לחצו על מילה בטקסט כדי להציג את הפירוש',
    noNotes: 'אין פירוש לקטע זה',
    // Shown on a note we could not place to a word. It describes OUR
    // uncertainty, not the note: the commentator was usually remarking on some
    // particular phrase, we just couldn't find which. The earlier wording ("on
    // the passage as a whole") asserted an editorial fact instead, and
    // contradicted itself on any note that displays a lemma -- most of them.
    unplaced: 'מקום מדויק בקטע לא זוהה',
    line: (ref) => `שורה ${ref}`,
};

export function createCommentaryPanel(options) {
    const {
        root,
        adapter,
    } = options;

    if (!root) throw new Error('createCommentaryPanel: `root` is required');
    if (!adapter || typeof adapter.entriesFor !== 'function') {
        throw new Error('createCommentaryPanel: `adapter.entriesFor` is required');
    }
    const labels = { ...COMMENTARY_LABELS, ...(options.labels || {}) };

    root.classList.add('mvc-comm');
    root.innerHTML = '';

    const heading = el('div', 'mvc-comm-heading');
    const list = el('div', 'mvc-comm-list');
    // Edition credit, shown once per panel rather than per note. Not decoration:
    // the Lieberman commentaries are CC-BY via Sefaria, and attribution is a
    // condition of the licence, so a panel that renders them must say whose text
    // it is showing.
    const credit = el('div', 'mvc-comm-credit');
    credit.hidden = true;
    root.append(heading, credit, list);

    let address = null;
    let renderedKey = null;

    // Notes are deliberately NOT clickable. Clicking a note used to scroll the
    // body text to the word it comments on, which had two problems: the scroll
    // moved the reader's position, which the scroll-follower then read as a new
    // passage and re-rendered the panel out from under them; and a commentary is
    // prose one naturally clicks INTO to select and copy, so making the whole
    // note a button fought the obvious gesture. If a "go to the text" affordance
    // is wanted, it should be an explicit small control on the note, not the
    // note itself.

    // Deliberately leaves the heading alone: callers decide whether it still
    // applies. "No notes here" needs to say *where* here is -- clearing it left
    // the reader unable to tell which passage had nothing said about it.
    function placeholder(text) {
        list.innerHTML = '';
        list.append(Object.assign(el('div', 'mvc-comm-empty'), { textContent: text }));
    }

    function render() {
        if (address == null) {
            renderedKey = null;
            heading.textContent = '';
            placeholder(labels.noSelection);
            return;
        }

        const entries = adapter.entriesFor(address) || [];
        heading.textContent = adapter.headingFor ? (adapter.headingFor(address) || '') : '';

        if (!entries.length) {
            renderedKey = null;
            placeholder(labels.noNotes);
            return;
        }

        // Re-render only when the set of notes actually changes. Moving from one
        // word to the next inside the same halakhah usually shows the same
        // notes, and rebuilding several hundred of them on every click would
        // both flicker and throw away the reader's scroll position.
        const key = entries.map(en => en.id).join('|');
        if (key !== renderedKey) {
            renderedKey = key;
            list.innerHTML = '';
            entries.forEach(entry => list.appendChild(buildNote(entry, labels)));
        }

        highlightActive(entries);
    }

    function highlightActive(entries) {
        list.querySelectorAll('.mvc-comm-note.active').forEach(n => n.classList.remove('active'));
        // Only a word-precise selection singles out a note; a halakhah-wide
        // selection is "show me everything here" and shouldn't pick a winner.
        if (scopeOf(address) !== WORD) return;
        const active = entries.filter(entry => entry.baseIdx != null && anchorMatches(entry, address));
        // Nothing covers this exact word -- the common case, since a commentary
        // remarks on selected words, not all of them. Raise the note anchored
        // nearest to where the reader is, so the panel tracks them instead of
        // sitting inert. (Nearest in either direction, not merely the preceding
        // one: clicking the first word of a passage would otherwise highlight
        // nothing at all.)
        const targets = active.length ? active : nearest(entries, address.baseIdx);
        let first = null;
        targets.forEach(entry => {
            const node = list.querySelector(`[data-entry-id="${cssEscape(entry.id)}"]`);
            if (!node) return;
            node.classList.add('active');
            if (!first) first = node;
        });
        if (first) first.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    render();

    return {
        // Edition credit: a string, or null to hide it. Set on context change,
        // since it belongs to the work being shown, not to the selection.
        setCredit(text) {
            credit.textContent = text || '';
            credit.hidden = !text;
        },
        select(addr) { address = addr; render(); },
        // A new work invalidates the rendered set even if the address happens to
        // look the same.
        setContext() { address = null; renderedKey = null; render(); },
        refresh() { renderedKey = null; render(); },
        get address() { return address; },
        destroy() {
            root.innerHTML = '';
            root.classList.remove('mvc-comm');
        },
    };
}

function nearest(entries, baseIdx) {
    let best = null;
    let bestDist = Infinity;
    entries.forEach(entry => {
        if (entry.baseIdx == null) return;
        const dist = Math.abs(entry.baseIdx - baseIdx);
        if (dist < bestDist) { bestDist = dist; best = entry; }
    });
    return best ? [best] : [];
}

function buildNote(entry, labels) {
    const node = el('div', 'mvc-comm-note');
    node.dataset.entryId = entry.id;
    if (entry.baseIdx == null) node.classList.add('mvc-comm-note-passage');

    // `metaLabel` lets the host word this per edition -- what an unplaced note
    // means differs between an authored dibbur hamatchil that didn't resolve and
    // an OCR chunk with no lemma at all.
    const metaText = entry.metaLabel
        || (entry.baseIdx == null ? labels.unplaced
            : (entry.lineRef ? labels.line(entry.lineRef) : null));
    if (metaText) {
        const meta = el('span', 'mvc-comm-meta');
        meta.textContent = metaText;
        node.appendChild(meta);
    }

    const body = el('div', 'mvc-comm-body');
    // The note's own markup (the DH in <b>, nested footnotes, references) comes
    // from the printed edition and is rendered as authored.
    body.innerHTML = entry.html || '';
    node.appendChild(body);
    return node;
}

function cssEscape(s) {
    return String(s).replace(/["\\]/g, '\\$&');
}

function el(tag, className) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    return node;
}
